import { useEffect, useRef, useCallback } from "react";
import ModalStyles from "./Modal.module.css";

type Props = {
  title: string;
  open: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
};

export const Modal = ({ title, open, onOpen, onClose, children }: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const hasInitialFocusRef = useRef(false);
  const titleId = "modal-title";

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    },
    [onClose]
  );

  // Focus trap within modal
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    
    // Don't interfere if focus is within a listbox (dropdown options)
    const activeElement = document.activeElement;
    if (activeElement?.closest('[role="listbox"]')) {
      return;
    }

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([role="option"])',
      'a[href]'
    ].join(', ');

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(focusableSelectors);
    
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }, []);

  // Set up keyboard event listeners
  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("keydown", handleFocusTrap);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [open, handleKeyDown, handleFocusTrap]);

  // Focus close button ONLY on initial mount, not on every re-render
  useEffect(() => {
    if (open && !hasInitialFocusRef.current) {
      hasInitialFocusRef.current = true;
      closeButtonRef.current?.focus();
    }
  }, [open]);

  // Reset the initial focus flag when modal closes
  useEffect(() => {
    if (!open) {
      hasInitialFocusRef.current = false;
    }
  }, [open]);

  if (!open) return null;

  return (
    <div 
      onClick={onClose} 
      className={ModalStyles.container}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={ModalStyles.subContainer}
        ref={modalRef}
      >
        <div className={ModalStyles.header}>
          <h1 id={titleId}>{title}</h1>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className={`fa-solid fa-xmark ${ModalStyles.closeButton}`}
            aria-label="Close modal"
          ></button>
        </div>

        <hr />

        {children}
      </div>
    </div>
  );
};
